import { makeAutoObservable } from 'mobx';
import * as subscriptions from '../../graphql/subscriptions';
import ErrorBoundaryStore from '../error-boundary-store/error-boundary-store';
import PollEntity, { IPollEntity } from './poll-entity';
import { TGraphQLSubscriptions } from '../../api/graphql-api-client/graphql-api-client';
import ErrorParsingService, { IErrorObject, IGraphQLErrorResponse } from '../../utils/services/error-parsing-service';
import ResponseEntity, { IResponseItem } from './response-entity';
import { INITIAL_VALUE, POLL_INCREMENT } from './../../constans/prioritize-constants';
import {
  TPollStatus,
  PollsTypes,
  PollsViewStatuses,
  TPollType,
  PollCommands,
  PollsTabsOnMobile,
  NUMBER_OF_RETRIES,
  DELAY,
  ITERATE_NUMBER
} from '../../constans/polls-constants';
import { getErrorBoundaryStore } from '../error-boundary-store/error-boundary-store-instanse';
import { getPodiumInfoStore } from '../podium-info-store/podium-info-store-instance';
import PodiumInfoStore from '../podium-info-store/podium-info-store';
import { getKeyByValue } from '../../utils/helpers/polls-helper';
import PresenterSlideManagementStore from '../presenter-slide-management-store/presenter-slide-management-store';
import { getPresenterSlideManagementStore } from '../presenter-slide-management-store/presenter-slide-management-store-instance';
import {
  createPollMutation,
  getPollsListQuery,
  ICommandResponse,
  ICreatePollResponse,
  IRequestPoll,
  ISubscriptionsData,
  pollCommandMutation,
  pollSubscription,
  updatePollAndChoicesMutation,
  getPollsChoicesQuery,
  deletePollMutation,
  updatePollMutation,
  generatePollResultsSlideApi,
  IPollSlideGenerateRequest,
  downloadPolls,
  IPollSlideGenerateResponse,
} from '../../api/dao/polls-dao';
import { getLivePresentationInfoApi, getAssetInfo } from './../../api/dao/presenter-slide-dao';
import { getMixpanelService } from '../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../constans/mixpanel-contants';

class PollsStore {
  public isReviewModalOpened: boolean;
  public isManagePollModalOpened: boolean;
  public pollsList: PollEntity[];
  public selectedPollId: string;
  public shouldShowNotificationModal: boolean;
  public isEllipsisMenuOpened: boolean;
  public isLoading: boolean;
  public shouldShowDeleteModal: boolean;
  public shouldShowResetModal: boolean;
  public shouldShowAddResultsSlideModal: boolean;
  public activeMobileTab: string;
  public isFullScreenModeEnabled: boolean;
  public errorParsingService: ErrorParsingService;
  public errorMessage: string;
  public isOnePollOpened: boolean;
  public livePresentationId: number;
  public isFullScreenModeSafari: boolean;

  constructor (
    private readonly podiumInfoStore: PodiumInfoStore = getPodiumInfoStore(),
    private readonly errorBoundary: ErrorBoundaryStore = getErrorBoundaryStore(),
    private readonly slideManagementStore: PresenterSlideManagementStore = getPresenterSlideManagementStore()
  ) {
    this.initialize();
  }

  public initialize = (): void => {
    makeAutoObservable(this);
    this.pollsList = [];
    this.selectedPollId = '';
    this.shouldShowNotificationModal = false;
    this.isEllipsisMenuOpened = false;
    this.shouldShowDeleteModal = false;
    this.shouldShowResetModal = false;
    this.shouldShowAddResultsSlideModal = false;
    this.activeMobileTab = PollsTabsOnMobile.LIST;
    this.errorParsingService = new ErrorParsingService();
    this.isFullScreenModeSafari = false;
  };

  public setIsFullScreenModeSafari = (value: boolean): void => {
    this.isFullScreenModeSafari = value;
  };

  public getPollsList = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;

    try {
      const result = await getPollsListQuery(presentationId);

      // TODO Need fix graphql response type
      // @ts-ignore
      const { data: { listPolls: { items }}} = result;
      this.pollsList = items.map((item: IPollEntity) => new PollEntity(item));
      this.setIsOnePollOpened(this.checkIsSomePollOpened());
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public createPoll = async (poll: PollEntity): Promise<void> => {
    try {
      const serializedPoll = { ...this.serializePollRequestDTO(poll) };
      await createPollMutation(serializedPoll);
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public onCreatePollSubscription = (): TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return pollSubscription(
      subscriptions.onCreatePoll,
      presentationId,
      this.onCreatePollCallback,
      () => this.onCreatePollSubscription());
  };

  public updatePoll = async (updateBody: Partial<IPollEntity>): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;

    try {
      await updatePollMutation(updateBody, presentationId);
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public onUpdatePollSubscription = (): TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return pollSubscription(
      subscriptions.onUpdatePoll,
      presentationId,
      this.updatePollCallback,
      () => this.onUpdatePollSubscription());
  };

  public pollCommand = async (commandBody: Partial<IPollEntity>, command: PollCommands): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if(!this.livePresentationId) {
      return;
    }

    try {
      this.isLoading = true;
      const result = await pollCommandMutation(commandBody, presentationId, this.livePresentationId, command);

      const parsedContent = JSON.parse(result.data?.pollCommand?.content as string);
      this.processPollCommand(parsedContent);
    } catch (error) {
      const { errors: [extractedError] } = error as IGraphQLErrorResponse;
      this.errorMessage = this.errorParsingService.extractGraphQLError(extractedError);

      //mixpanel track ended error
      if(this.errorMessage.match('already ended')) {
        getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.ERROR_ENDED_LIVE);
      }
      if(this.errorMessage.match('already open')) {
        getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.ERROR_ONE_POLL_OPENED);
      }
    } finally {
      this.isLoading = false;
    }
  };

  public getLiveInfoForCommand = async (): Promise<void> => {
    this.livePresentationId = this.slideManagementStore.returnLivePresentationInfo()?.id || 0;
    if (!this.livePresentationId) {
      const presentationId = this.podiumInfoStore.getPresentationId() || '';
      try {
        const result = await getLivePresentationInfoApi(presentationId);
        this.livePresentationId = result.id;
      // eslint-disable-next-line no-empty
      } catch(e) {
      }
    }
  };

  public getPollChoisesList = async (pollId: string | null): Promise<ResponseEntity[] | void> => {
    if (!pollId) return;
    this.selectedPollId = pollId;
    try {
      const result = await getPollsChoicesQuery(pollId);

      // TODO Need fix graphql response type
      // @ts-ignore
      const { data: { listPollChoices }} = result;
      const selectedPoll = this.getPollEntityById(pollId);
      selectedPoll.countVotes = listPollChoices.allVotesCount;
      selectedPoll.responses = listPollChoices.pollChoicesCountedList.map((el: IResponseItem) => new ResponseEntity(el));
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    } finally {
      this.isLoading = false;
    }
  };

  public deletePoll = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!this.selectedPollId || !presentationId) return;
    const deletedId = this.selectedPollId;
    try {
      await deletePollMutation(deletedId, presentationId);

      this.pollsList = this.pollsList.filter((el: IPollEntity) => el.pollId !== deletedId);
      this.selectedPollId = '';
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public onDeletePollSubscription = (): TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return pollSubscription(
      subscriptions.onDeletePoll,
      presentationId,
      this.deletePollCallback,
      () => this.onDeletePollSubscription());
  };

  public onSavePollVotesSubscription = (): TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return pollSubscription(
      subscriptions.onSavePollVotes,
      presentationId,
      this.savePollVotesCallback,
      () => this.onSavePollVotesSubscription());
  };

  public changePollStatus = async (poll: PollEntity, customStatus?: PollsViewStatuses): Promise<void> => {
    const status = customStatus || poll.getNextPollStatus();
    const statusForUpdate = getKeyByValue(PollsViewStatuses, status) as TPollStatus;
    const command = poll.getCommand(PollsViewStatuses[statusForUpdate]);
    const updatePollBody = {
      status: statusForUpdate || getKeyByValue(PollsViewStatuses, PollsViewStatuses.NOT_STARTED),
      pollId: poll.getId(),
      value: poll.value,
      type: getKeyByValue(PollsTypes, poll.type) as TPollType
    };

    await this.pollCommand(updatePollBody, command);
  };

  public updatePollAndPollChoices = async (poll: PollEntity): Promise<void> => {
    try {
      const presentationId = this.podiumInfoStore.getPresentationId();
      const serializedPoll = { ...this.serializePollRequestDTO(poll) };
      await updatePollAndChoicesMutation(presentationId, serializedPoll.poll, serializedPoll.pollChoices);
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  private getAssetInfoWithRetry = async (param: number, numberOfRetries = NUMBER_OF_RETRIES, delay = DELAY): Promise<IPollSlideGenerateResponse> => {
    const response = await getAssetInfo(param);
    const condition = response.conversion_state === 'conversion_complete' && response.processing_state === 'processing_complete';
    if (condition) {
      return response;
    } else {
      if (numberOfRetries !== 0) {
        await new Promise(resolve => setTimeout(resolve, delay));
        return await this.getAssetInfoWithRetry(param, numberOfRetries - ITERATE_NUMBER);
      } else {
        throw Error();
      }
    }
  };

  public generatePollResultsSlide = async (pollSlideGenerateBody: IPollSlideGenerateRequest, pollBody: Partial<IPollEntity>): Promise<void> => {
    try {
      const response = await generatePollResultsSlideApi(pollSlideGenerateBody);
      const assetResponse: IPollSlideGenerateResponse = await this.getAssetInfoWithRetry(response?.id);
      const relatedAssets = assetResponse?.related_assets[0];
      const smallThumbnail = relatedAssets ? relatedAssets.metadata.thumbnail_url : assetResponse?.thumbnail_url;
      const options = {
        smallThumbnail,
        largeThumbnail: assetResponse?.thumbnail_url,
        assetId: response?.id
      };

      await this.slideManagementStore.publishSegments(options);
      await this.updatePoll(pollBody);

      getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.ADD_RESULTS_SLIDE);
    } catch (e) {
      this.errorMessage = this.errorParsingService.parseApiErrorMessage(e as IErrorObject);
    }
  };

  public hideNotificationModal = (): void => {
    this.shouldShowNotificationModal = false;
  };

  public setShouldShowEllipsisMenu = (value: boolean): void => {
    this.isEllipsisMenuOpened = value;
  };

  public setShouldShowDeleteModal = (value: boolean): void => {
    this.shouldShowDeleteModal = value;
  };

  public setShouldShowAddResultsSlideModal = (value: boolean): void => {
    this.shouldShowAddResultsSlideModal = value;
  };

  public toggleManagePollModal = (): void => {
    this.isManagePollModalOpened = !this.isManagePollModalOpened;
  };

  public setShouldShowFullScreen = (value: boolean): void => {
    this.isFullScreenModeEnabled = value;
  };

  public setShouldShowResetModal = (value: boolean): void => {
    this.shouldShowResetModal = value;
  };

  public setActiveTab = (value: string): void => {
    this.activeMobileTab = value;
  };

  public setIsOnePollOpened = (value: boolean): void => {
    this.isOnePollOpened = value;
  };

  public clearErrorMessage = (): void => {
    this.errorMessage = '';
  };

  public checkIsSomePollOpened = (): boolean => {
    return this.pollsList.some((poll: PollEntity) => poll.status === PollsViewStatuses.IN_PROGRESS || poll.shouldShowClosingProgress);
  };

  public getPollEntityById = (id?: string): PollEntity => {
    const index = this.pollsList.findIndex((el: PollEntity) => el.getId() === id);
    return this.pollsList[index];
  };

  private onCreatePollCallback = (data: Pick<ISubscriptionsData, 'onCreatePoll'>): void => {
    const serializedData = this.serializePollResponse(data.onCreatePoll);
    this.updateListOnCreate(serializedData);
  };

  private updatePollCallback = (data: Pick<ISubscriptionsData, 'onUpdatePoll'>): void => {
    const serializedData = data.onUpdatePoll;
    this.updatePollOnSubscription(serializedData);
  };

  private updatePollOnSubscription = (data: IPollEntity): void => {
    const pollEntity = this.getPollEntityById(data.pollId);
    data.responses = [...pollEntity.responses];
    data.countVotes = pollEntity.responses.length
      ? this.checkTotalCountsBasedOnResponsesCount(pollEntity)
      : data.countVotes;

    pollEntity.deserialize(data);
    pollEntity.updateIsShowClosingProgress();
    this.setIsOnePollOpened(this.checkIsSomePollOpened());
  };

  private deletePollCallback = (data: Pick<ISubscriptionsData, 'onDeletePoll'>): void => {
    const { onDeletePoll: { Polls: polls }} = data;
    const deletedId = polls[0].pollId;
    this.pollsList = this.pollsList.filter((el: PollEntity) => el.getId() !== deletedId);
  };

  private savePollVotesCallback = (data: Pick<ISubscriptionsData, 'onSavePollVotes'>): void => {
    const { onSavePollVotes: voteResults } = data;
    const openedPoll = this.getPollEntityById(voteResults.pollId);
    openedPoll.countVotes = voteResults.allVotesCount;
    openedPoll.snapshot.countVotes = voteResults.allVotesCount;
    openedPoll.setResponseVoteResults(voteResults.pollVotesCount);
  };

  private processPollCommand = (data: ICommandResponse) => {
    const pollEntity = this.getPollEntityById(data.poll_id);
    pollEntity.publicAdjustment = data.public_adjustment;
    pollEntity.pollStatusTimestamp = data.poll_status_timestamp;
    pollEntity.status = PollsViewStatuses[data.status as TPollStatus];
    pollEntity.snapshot.poll_completed_timestamp = data.poll_status_timestamp;
    pollEntity.updateIsShowClosingProgress();
  };

  private updateListOnCreate = (fromDTO: IPollEntity): void => {
    const isEditMode = this.pollsList.find(el => el.getId() === fromDTO.pollId);
    fromDTO.responses = fromDTO.responses?.map(el => new ResponseEntity(el));

    if (!isEditMode) {
      const newPoll = new PollEntity(fromDTO);
      this.pollsList.push(newPoll);
      this.selectedPollId = fromDTO.pollId || '';
    } else {
      const editedPoll = this.getPollEntityById(fromDTO.pollId);
      editedPoll.deserialize(fromDTO);
    }
  };

  private getPollPrioritizeIndex = (poll: PollEntity, index?: number): number => {
    const minIndexInArray = this.pollsList.length && Math.min(...this.pollsList.map(el => el.prioritizeIndex));
    const lastPrioritizeIndex = minIndexInArray || INITIAL_VALUE;
    const STEP = lastPrioritizeIndex > INITIAL_VALUE ? INITIAL_VALUE : POLL_INCREMENT;

    return index || poll.prioritizeIndex || lastPrioritizeIndex - STEP;
  };

  private serializePollRequestDTO = (poll: PollEntity, index?: number): IRequestPoll => {
    const newOrder = this.getPollPrioritizeIndex(poll, index);
    const presentationId = this.podiumInfoStore.getPresentationId();

    return {
      pollChoices: poll.serialize().responses?.map((el: ResponseEntity) => el.serialize()) || [],
      poll: {
        pollId: poll.getId(),
        hideForPresenter: poll.hideForPresenter,
        order: newOrder,
        status: getKeyByValue(PollsViewStatuses, poll.status) as TPollStatus,
        type: getKeyByValue(PollsTypes, poll.type) as TPollType,
        value: poll.value,
        result_slide_added: poll.result_slide_added,
      },
      presentationId: presentationId ?? ''
    };
  };

  private serializePollResponse = (data: ICreatePollResponse): IPollEntity => {
    const pollEntityDTO = { ...data.Polls[0] };
    pollEntityDTO.status = PollsViewStatuses[pollEntityDTO.status as TPollStatus];
    pollEntityDTO.type = PollsTypes[pollEntityDTO.type as TPollType];

    return {
      ...pollEntityDTO,
      responses: [...data.PollChoices]
    };
  };

  public downloadPollsCSV = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;

    try {
      await downloadPolls(presentationId);
    } catch (error) {
      this.errorBoundary.setErrorMessage(error as IErrorObject);
    }
  };

  //TODO: Delete it when will be total counts for update subscription
  private checkTotalCountsBasedOnResponsesCount = (targetEntity: PollEntity) => {
    let count = 0;
    for (const response of targetEntity.responses) {
      count += response.count || 0;
    }
    return count;
  };
}

export default PollsStore;
